import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useLocation, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { ArrowLeft, FileText, Plus, Save, Trash2 } from 'lucide-react'
import toast from 'react-hot-toast'
import Swal from 'sweetalert2'

import type { Account, ItemEntity, Warehouse } from '@/api/types'
import { inventoryMovementService } from '@/services/inventoryMovementService'
import { inventoryAdjustmentService } from '@/services/inventoryAdjustmentService'
import type { AdjustmentBatchRow, InventoryAdjustmentSavePayload } from '@/services/inventoryAdjustmentService'
import { accountsService } from '@/services/accountsService'
import { useAuthStore } from '@/store/auth.store'
import { useSettingsStore } from '@/store/settings.store'

import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import type { AdjustmentLine } from '../types'
import { newAdjustmentLine, quantityAdjusted } from '../types'

const SELECT_EMPTY = '__inv_adj_empty__'
function warehouseLabel(w: Warehouse): string {
  const c = w.code?.trim()
  const n = w.name?.trim()
  if (c && n) return `${c} — ${n}`
  return n || c || String(w.id)
}

function accountLabel(acc: Account, lang: string): string {
  const code = acc.code?.trim() || ''
  const name = (
    lang === 'ar' ? acc.nameAr || acc.nameEn : acc.nameEn || acc.nameAr || acc.name
  )?.trim() || ''
  if (code && name) return `${code} — ${name}`
  return name || code || String(acc.accountId ?? acc.id ?? '')
}

function todayIso() {
  return new Date().toISOString().slice(0, 10)
}

export const InventoryAdjustmentEdit: React.FC = () => {
  const { t } = useTranslation('common')
  const navigate = useNavigate()
  const { id } = useParams<{ id: string }>()
  const location = useLocation()
  const { user } = useAuthStore()
  const { language = 'en' } = useSettingsStore()
  const selectedBranchId = Number(user?.selectedBranch?.branchId ?? 0)
  const prevBranchIdRef = useRef(0)

  const adjustmentIdNum = useMemo(() => {
    const n = Number(id)
    return Number.isFinite(n) && n > 0 ? n : 0
  }, [id])
  const isNew = adjustmentIdNum <= 0
  const viewOnly = !isNew && location.pathname.endsWith('/view')

  const [detailLoading, setDetailLoading] = useState(false)
  const [adjustmentDate, setAdjustmentDate] = useState(todayIso())
  const [warehouseId, setWarehouseId] = useState(SELECT_EMPTY)
  const [reference, setReference] = useState('')
  const [adjustmentCode, setAdjustmentCode] = useState('')
  const [description, setDescription] = useState('')
  const [reason, setReason] = useState('')
  const [costAccountId, setCostAccountId] = useState(SELECT_EMPTY)
  const [modeOfAdjustment, setModeOfAdjustment] = useState(0)
  const [modeOfAdjustmentValue, setModeOfAdjustmentValue] = useState(0)
  const [adjustmentType, setAdjustmentType] = useState(0)
  const [attachmentFilePath, setAttachmentFilePath] = useState('')
  /** From GET master; 0 = fall back to selected branch on save */
  const [persistBranchId, setPersistBranchId] = useState(0)
  const [warehouses, setWarehouses] = useState<Warehouse[]>([])
  const [warehousesLoading, setWarehousesLoading] = useState(false)
  const [accounts, setAccounts] = useState<Account[]>([])
  const [accountsLoading, setAccountsLoading] = useState(false)

  const [items, setItems] = useState<ItemEntity[]>([])
  const [itemsLoading, setItemsLoading] = useState(false)
  const [batchesByLineId, setBatchesByLineId] = useState<Record<string, AdjustmentBatchRow[]>>({})
  const [batchesLoadingByLineId, setBatchesLoadingByLineId] = useState<Record<string, boolean>>({})
  const [qtyLoadingByLineId, setQtyLoadingByLineId] = useState<Record<string, boolean>>({})
  const [saveLoading, setSaveLoading] = useState(false)

  const [lines, setLines] = useState<AdjustmentLine[]>(() =>
    Array.from({ length: 5 }, () => newAdjustmentLine())
  )

  const itemOptions = useMemo(() => {
    return items.map((it) => {
      const name =
        language === 'ar'
          ? it.itemNameAr?.trim() || it.itemNameEn?.trim()
          : it.itemNameEn?.trim() || it.itemNameAr?.trim()
      const code = it.itemCode?.trim() || ''
      const label = `${code}${code && name ? ' — ' : ''}${name || ''}`.trim()
      return { value: String(it.itemId), label: label || String(it.itemId) }
    })
  }, [items, language])

  useEffect(() => {
    if (isNew || adjustmentIdNum <= 0) return

    let cancelled = false
    setDetailLoading(true)
    void Promise.all([
      inventoryAdjustmentService.getAdjustmentById(adjustmentIdNum),
      inventoryAdjustmentService.getAdjustmentLinesByAdjustmentId(adjustmentIdNum),
    ])
      .then(([master, lineList]) => {
        if (cancelled) return

        const dateStr = master.adjustmentDate?.slice(0, 10) || todayIso()
        setAdjustmentDate(dateStr)
        setAdjustmentCode(master.code || '')
        setReference(master.reference || '')
        setDescription(master.description || '')
        setReason(master.reason || '')
        setModeOfAdjustment(Number.isFinite(master.modeOfAdjustment) ? master.modeOfAdjustment : 0)
        setModeOfAdjustmentValue(Number.isFinite(master.modeOfAdjustmentValue) ? master.modeOfAdjustmentValue : 0)
        setAdjustmentType(Number.isFinite(master.adjustmentType) ? master.adjustmentType : 0)
        setAttachmentFilePath(master.attachmentFilePath || '')
        setPersistBranchId(master.branchId > 0 ? master.branchId : 0)

        setCostAccountId(
          master.costAccount > 0 ? String(master.costAccount) : SELECT_EMPTY,
        )

        const wh = master.warehouseId > 0 ? String(master.warehouseId) : SELECT_EMPTY
        setWarehouseId(wh)

        const whNum = master.warehouseId
        const mappedLines: AdjustmentLine[] = lineList.map((row, idx) => ({
          id: `loaded-${idx}-${row.itemId}-${row.batchId}`,
          apiLineId: row.id > 0 ? row.id : undefined,
          itemId: row.itemId > 0 ? String(row.itemId) : '',
          batchId: row.batchId > 0 ? String(row.batchId) : '',
          currentQty: Number.isFinite(row.previousQuantity) ? row.previousQuantity : 0,
          newQty: Number.isFinite(row.newQuantity) ? row.newQuantity : 0,
        }))

        if (mappedLines.length === 0) {
          setLines(Array.from({ length: 5 }, () => newAdjustmentLine()))
        } else {
          setLines(mappedLines)
        }

        if (whNum > 0) {
          lineList.forEach((row, idx) => {
            const lineKey = `loaded-${idx}-${row.itemId}-${row.batchId}`
            if (row.itemId > 0) {
              void inventoryAdjustmentService
                .getBatchesByWarehouseAndItem(whNum, row.itemId)
                .then((batches) => {
                  if (cancelled) return
                  setBatchesByLineId((prev) => ({ ...prev, [lineKey]: batches }))
                })
                .catch(() => {})
            }
          })
        }
      })
      .catch((e: unknown) => {
        if (cancelled) return
        const msg =
          e && typeof e === 'object' && 'response' in e
            ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
            : undefined
        toast.error(msg || t('inventoryAdjustments.detailLoadFailed'))
      })
      .finally(() => {
        if (!cancelled) setDetailLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [adjustmentIdNum, isNew, t])

  useEffect(() => {
    if (!selectedBranchId) {
      prevBranchIdRef.current = 0
      setWarehouses([])
      setWarehouseId(SELECT_EMPTY)
      setCostAccountId(SELECT_EMPTY)
      return
    }
    if (prevBranchIdRef.current !== 0 && prevBranchIdRef.current !== selectedBranchId) {
      setWarehouseId(SELECT_EMPTY)
      setCostAccountId(SELECT_EMPTY)
    }
    prevBranchIdRef.current = selectedBranchId

    let cancelled = false
    setWarehousesLoading(true)
    void inventoryMovementService
      .getWarehousesByBranchId(selectedBranchId)
      .then((list) => {
        if (!cancelled) setWarehouses(list)
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setWarehouses([])
          const msg =
            e && typeof e === 'object' && 'response' in e
              ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
              : undefined
          toast.error(msg || t('unsellableInventory.warehousesLoadFailed'))
        }
      })
      .finally(() => {
        if (!cancelled) setWarehousesLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [selectedBranchId, t])

  useEffect(() => {
    const wh = Number(warehouseId)
    if (!wh || warehouseId === SELECT_EMPTY) {
      setItems([])
      setBatchesByLineId({})
      setBatchesLoadingByLineId({})
      setQtyLoadingByLineId({})
      setLines((prev) => prev.map((l) => ({ ...l, itemId: '', batchId: '', currentQty: 0, newQty: 0 })))
      return
    }

    let cancelled = false
    setItemsLoading(true)
    void inventoryAdjustmentService
      .getItemsByWarehouse(wh)
      .then((list) => {
        if (!cancelled) setItems(list)
      })
      .catch((e: unknown) => {
        if (!cancelled) {
          setItems([])
          const msg =
            e && typeof e === 'object' && 'response' in e
              ? (e as { response?: { data?: { message?: string } } }).response?.data?.message
              : undefined
          toast.error(msg || t('unsellableInventory.itemsLoadFailed'))
        }
      })
      .finally(() => {
        if (!cancelled) setItemsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [warehouseId, t])

  useEffect(() => {
    if (!selectedBranchId) {
      setAccounts([])
      return
    }

    let cancelled = false
    setAccountsLoading(true)
    void accountsService
      .getTransactionableAccounts(selectedBranchId)
      .then((list) => {
        if (!cancelled) setAccounts(list || [])
      })
      .catch(() => {
        if (!cancelled) {
          setAccounts([])
          toast.error(t('chartsOfAccounts.failedToLoadAccounts'))
        }
      })
      .finally(() => {
        if (!cancelled) setAccountsLoading(false)
      })

    return () => {
      cancelled = true
    }
  }, [selectedBranchId, t])

  const handleBack = () => navigate('/inventory/adjustments')

  const readonlyFieldClass = 'focus-visible:ring-0 focus-visible:ring-offset-0'

  const warehouseDisplay = useMemo(() => {
    if (warehouseId === SELECT_EMPTY) return ''
    const w = warehouses.find((x) => String(x.id) === warehouseId)
    return w ? warehouseLabel(w) : ''
  }, [warehouseId, warehouses])

  const costAccountDisplay = useMemo(() => {
    if (costAccountId === SELECT_EMPTY) return ''
    const acc = accounts.find((a) => String(a.accountId ?? a.id) === costAccountId)
    return acc ? accountLabel(acc, language) : ''
  }, [costAccountId, accounts, language])

  const handleWarehouseChange = (v: string) => {
    setWarehouseId(v)
    setBatchesByLineId({})
    setBatchesLoadingByLineId({})
    setQtyLoadingByLineId({})
    setLines((prev) => prev.map((l) => ({ ...l, itemId: '', batchId: '', currentQty: 0, newQty: 0 })))
  }

  const addLines = (n: number) => {
    setLines((prev) => [...prev, ...Array.from({ length: n }, () => newAdjustmentLine())])
  }

  const loadBatchesForLine = (lineId: string, whId: number, itemIdNum: number) => {
    setBatchesLoadingByLineId((prev) => ({ ...prev, [lineId]: true }))
    void inventoryAdjustmentService
      .getBatchesByWarehouseAndItem(whId, itemIdNum)
      .then((rows) => {
        setBatchesByLineId((prev) => ({ ...prev, [lineId]: rows }))
      })
      .catch(() => {
        toast.error(t('unsellableInventory.batchesLoadFailed'))
        setBatchesByLineId((prev) => ({ ...prev, [lineId]: [] }))
      })
      .finally(() => {
        setBatchesLoadingByLineId((prev) => ({ ...prev, [lineId]: false }))
      })
  }

  const handleItemChange = (index: number, value: string) => {
    let lineId = ''
    setLines((prev) => {
      const next = [...prev]
      lineId = next[index].id
      if (value === SELECT_EMPTY) {
        next[index] = { ...next[index], itemId: '', batchId: '', currentQty: 0, newQty: 0 }
      } else {
        next[index] = { ...next[index], itemId: value, batchId: '', currentQty: 0, newQty: 0 }
      }
      return next
    })

    setBatchesByLineId((prev) => ({ ...prev, [lineId]: [] }))
    setQtyLoadingByLineId((prev) => ({ ...prev, [lineId]: false }))

    if (value === SELECT_EMPTY) return

    const wh = Number(warehouseId)
    const itemNum = Number(value)
    if (wh && itemNum) loadBatchesForLine(lineId, wh, itemNum)
  }

  const handleBatchChange = (index: number, value: string) => {
    let lineId = ''
    let snapshotItemId = ''
    setLines((prev) => {
      const next = [...prev]
      lineId = next[index].id
      snapshotItemId = next[index].itemId
      if (value === SELECT_EMPTY) {
        next[index] = { ...next[index], batchId: '', currentQty: 0, newQty: 0 }
      } else {
        next[index] = { ...next[index], batchId: value, currentQty: 0, newQty: 0 }
      }
      return next
    })

    if (value === SELECT_EMPTY) {
      setQtyLoadingByLineId((prev) => ({ ...prev, [lineId]: false }))
      return
    }

    const wh = Number(warehouseId)
    const itemNum = Number(snapshotItemId)
    const bNum = Number(value)
    if (!wh || !itemNum || !bNum) return

    setQtyLoadingByLineId((prev) => ({ ...prev, [lineId]: true }))
    void inventoryAdjustmentService
      .getItemDetails(wh, itemNum, bNum)
      .then(({ availableQuantity }) => {
        setLines((prev) => {
          const idx = prev.findIndex((l) => l.id === lineId)
          if (idx < 0) return prev
          if (prev[idx].batchId !== value) return prev
          const n = [...prev]
          n[idx] = { ...n[idx], currentQty: availableQuantity, newQty: availableQuantity }
          return n
        })
      })
      .catch(() => {
        toast.error(t('unsellableInventory.itemDetailsLoadFailed'))
        setLines((prev) => {
          const idx = prev.findIndex((l) => l.id === lineId)
          if (idx < 0) return prev
          const n = [...prev]
          n[idx] = { ...n[idx], currentQty: 0, newQty: 0 }
          return n
        })
      })
      .finally(() => {
        setQtyLoadingByLineId((prev) => ({ ...prev, [lineId]: false }))
      })
  }

  const updateLineNewQty = (index: number, newQty: number) => {
    const n = Number(newQty)
    setLines((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], newQty: Number.isFinite(n) ? n : 0 }
      return next
    })
  }

  function formatQuantityAdjusted(delta: number): string {
    if (!Number.isFinite(delta)) return ''
    if (delta > 0) return `+${delta}`
    return String(delta)
  }

  const removeLine = (index: number) => {
    const lineId = lines[index]?.id
    setLines((prev) => prev.filter((_, i) => i !== index))
    if (lineId) {
      setBatchesByLineId((prev) => {
        const { [lineId]: _, ...rest } = prev
        return rest
      })
      setBatchesLoadingByLineId((prev) => {
        const { [lineId]: __, ...rest } = prev
        return rest
      })
      setQtyLoadingByLineId((prev) => {
        const { [lineId]: ___, ...rest } = prev
        return rest
      })
    }
  }

  function batchLabel(b: AdjustmentBatchRow): string {
    const c = b.code?.trim()
    return c || String(b.id)
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (viewOnly) return
    if (!selectedBranchId) {
      toast.error(t('unsellableInventory.saveNoBranch'))
      return
    }
    if (!warehouseId || warehouseId === SELECT_EMPTY) {
      toast.error(t('inventoryAdjustments.validationWarehouse'))
      return
    }

    const whNum = Number(warehouseId)
    const filledLines = lines.filter((l) => l.itemId && l.batchId)
    if (filledLines.length === 0) {
      toast.error(t('inventoryAdjustments.validationLines'))
      return
    }

    const headerId = isNew ? 0 : adjustmentIdNum
    const costNum =
      costAccountId === SELECT_EMPTY || !costAccountId ? 0 : Number(costAccountId)

    const itemList = filledLines.map((l) => {
      const pq = Number(l.currentQty)
      const nq = Number(l.newQty)
      const previousQuantity = Number.isFinite(pq) ? pq : 0
      const newQuantity = Number.isFinite(nq) ? nq : 0
      return {
        id: l.apiLineId ?? 0,
        adjustmentId: headerId,
        itemId: Number(l.itemId),
        batchId: Number(l.batchId),
        warehouseId: whNum,
        previousQuantity,
        newQuantity,
        quantityAdjusted: quantityAdjusted(l),
        isDeleted: 'false',
      }
    })

    const branchIdSave = persistBranchId > 0 ? persistBranchId : selectedBranchId

    const payload: InventoryAdjustmentSavePayload = {
      id: headerId,
      code: adjustmentCode.trim() || reference.trim(),
      modeOfAdjustment,
      modeOfAdjustmentValue,
      adjustmentType,
      reference: reference.trim(),
      adjustmentDate: adjustmentDate.slice(0, 10),
      costAccount: Number.isFinite(costNum) ? costNum : 0,
      reason: reason.trim(),
      warehouseId: whNum,
      branchId: branchIdSave,
      description: description.trim(),
      attachmentFilePath: attachmentFilePath.trim(),
      itemList,
    }

    console.log('[InventoryAdjustment] save payload (JSON)', JSON.stringify(payload, null, 2))
    console.log('[InventoryAdjustment] save payload (object)', payload)

    setSaveLoading(true)
    try {
      await inventoryAdjustmentService.save(payload)
      await Swal.fire({
        icon: 'success',
        title: t('common.success'),
        text: t('inventoryAdjustments.saveSuccess'),
        confirmButtonText: t('common.ok'),
      })
      navigate('/inventory/adjustments')
    } catch (err: unknown) {
      const anyErr = err as any
      const response = anyErr?.response
      const request = anyErr?.config

      console.group('%c[InventoryAdjustment] SAVE FAILED', 'color:#ef4444;font-weight:bold;')
      console.log('Error object:', err)
      if (err instanceof Error) {
        console.log('Error name :', err.name)
        console.log('Error msg  :', err.message)
        console.log('Stack      :', err.stack)
      }
      if (request) {
        console.log('Request URL    :', request.baseURL ? `${request.baseURL}${request.url}` : request.url)
        console.log('Request method:', request.method)
        console.log('Request body  :', request.data)
        console.log('Request headers:', request.headers)
      }
      if (response) {
        console.log('Status     :', response.status, response.statusText)
        console.log('Response headers:', response.headers)
        try {
          console.log('Response data (JSON):\n' + JSON.stringify(response.data, null, 2))
        } catch {
          console.log('Response data (raw):', response.data)
        }
      } else {
        console.log('No HTTP response attached to error (network or pre-flight failure).')
      }
      console.groupEnd()

      let msg = ''
      if (response) {
        const data = response.data
        if (typeof data === 'string') msg = data
        else if (data && typeof data === 'object') {
          const d = data as Record<string, unknown>
          msg = String(d.message ?? d.Message ?? d.msg ?? d.Msg ?? d.error ?? d.Error ?? '')
          if (!msg.trim() && Array.isArray((d as any).errors)) {
            msg = (d as any).errors.map((e: any) => e?.message ?? String(e)).join(', ')
          }
          if (!msg.trim() && (d as any).errors && typeof (d as any).errors === 'object') {
            msg = Object.entries((d as any).errors)
              .map(([k, v]) => `${k}: ${Array.isArray(v) ? v.join(', ') : String(v)}`)
              .join(' | ')
          }
          if (!msg.trim() && typeof (d as any).title === 'string') msg = String((d as any).title)
        }
      }
      if (!msg.trim() && err instanceof Error) msg = err.message

      // Backend currently returns HTTP 400 with the LINQ "Sequence contains more than one matching element"
      // message even though the record is actually persisted. Treat it as a success so the user
      // sees the normal success swal and navigates back to the list. Remove once the API is fixed.
      const isSpuriousSuccess =
        response?.status === 400 &&
        /sequence contains more than one matching element/i.test(msg)

      if (isSpuriousSuccess) {
        console.warn(
          '[InventoryAdjustment] Backend returned 400 with a known false-failure message; treating as success.'
        )
        await Swal.fire({
          icon: 'success',
          title: t('common.success'),
          text: t('inventoryAdjustments.saveSuccess'),
          confirmButtonText: t('common.ok'),
        })
        navigate('/inventory/adjustments')
        return
      }

      await Swal.fire({
        icon: 'error',
        title: t('common.error'),
        text: msg.trim() || t('inventoryAdjustments.saveFailed'),
        confirmButtonText: t('common.ok'),
      })
    } finally {
      setSaveLoading(false)
    }
  }

  const tableHeaderClass =
    'h-11 border-0 bg-primary/5 text-xs font-semibold uppercase tracking-wide text-primary hover:bg-primary/5 dark:bg-primary/10 dark:text-primary'

  return (
    <div className="space-y-6 p-4 sm:p-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="flex gap-3">
          <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">{t('inventoryAdjustments.formTitle')}</h1>
            <p className="text-muted-foreground text-sm">{t('inventoryAdjustments.formSubtitle')}</p>
            {detailLoading && (
              <p className="mt-1 text-sm text-muted-foreground">
                {t('common.loading', { defaultValue: 'Loading…' })}
              </p>
            )}
          </div>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <Card>
          <CardHeader className="border-b pb-4">
            <CardTitle className="text-lg">{t('inventoryAdjustments.sectionAdjustmentDetails')}</CardTitle>
          </CardHeader>
          <CardContent className="grid grid-cols-1 gap-4 pt-6 md:grid-cols-3">
            <div className="space-y-2">
              <Label htmlFor="adj-date">{t('inventoryAdjustments.adjustmentDate')}</Label>
              <Input
                id="adj-date"
                type="date"
                value={adjustmentDate}
                readOnly={viewOnly}
                className={viewOnly ? readonlyFieldClass : undefined}
                onChange={(e) => setAdjustmentDate(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>
                {t('inventoryAdjustments.warehouse')} <span className="text-destructive">*</span>
              </Label>
              {viewOnly ? (
                <Input
                  value={warehouseDisplay || (warehousesLoading ? t('common.loading', { defaultValue: 'Loading…' }) : '')}
                  readOnly
                  className={readonlyFieldClass}
                />
              ) : (
                <Select
                  value={warehouseId}
                  onValueChange={handleWarehouseChange}
                  disabled={!selectedBranchId || warehousesLoading || detailLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('inventoryAdjustments.selectWarehouse')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SELECT_EMPTY}>
                      {!selectedBranchId
                        ? t('unsellableInventory.selectBranchForWarehouses')
                        : warehousesLoading
                          ? t('common.loading', { defaultValue: 'Loading…' })
                          : t('inventoryAdjustments.selectWarehouse')}
                    </SelectItem>
                    {warehouses.map((w) => (
                      <SelectItem key={w.id} value={String(w.id)}>
                        {warehouseLabel(w)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>
            <div className="space-y-2">
              <Label htmlFor="adj-ref">{t('inventoryAdjustments.reference')}</Label>
              <Input
                id="adj-ref"
                value={reference}
                readOnly={viewOnly}
                className={viewOnly ? readonlyFieldClass : undefined}
                onChange={(e) => setReference(e.target.value)}
              />
            </div>
            <div className="space-y-2 md:col-span-1">
              <Label htmlFor="adj-desc">{t('inventoryAdjustments.description')}</Label>
              <Input
                id="adj-desc"
                value={description}
                readOnly={viewOnly}
                className={viewOnly ? readonlyFieldClass : undefined}
                onChange={(e) => setDescription(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="adj-reason">{t('inventoryAdjustments.reason')}</Label>
              <Input
                id="adj-reason"
                value={reason}
                readOnly={viewOnly}
                className={viewOnly ? readonlyFieldClass : undefined}
                onChange={(e) => setReason(e.target.value)}
              />
            </div>
            <div className="space-y-2">
              <Label>{t('inventoryAdjustments.costAccount')}</Label>
              {viewOnly ? (
                <Input
                  value={costAccountDisplay || (accountsLoading ? t('chartsOfAccounts.loadingAccounts') : '')}
                  readOnly
                  className={readonlyFieldClass}
                />
              ) : (
                <Select
                  value={costAccountId}
                  onValueChange={setCostAccountId}
                  disabled={!selectedBranchId || accountsLoading}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={t('inventoryAdjustments.selectCostAccount')} />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value={SELECT_EMPTY}>
                      {!selectedBranchId
                        ? t('chartsOfAccounts.selectBranchToViewAccounts')
                        : accountsLoading
                          ? t('chartsOfAccounts.loadingAccounts')
                          : t('inventoryAdjustments.selectCostAccount')}
                    </SelectItem>
                    {accounts.map((acc) => {
                      const id = acc.accountId ?? acc.id
                      return (
                        <SelectItem key={id} value={String(id)}>
                          {accountLabel(acc, language)}
                        </SelectItem>
                      )
                    })}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden">
          <CardHeader className="flex flex-col gap-4 border-b pb-4 sm:flex-row sm:items-center sm:justify-between">
            <CardTitle className="text-lg">{t('inventoryAdjustments.sectionAdjustedItems')}</CardTitle>
            {!viewOnly && (
              <div className="flex flex-wrap gap-2">
                <Button type="button" variant="outline" size="sm" onClick={() => addLines(1)}>
                  <Plus className="mr-1 h-4 w-4" />
                  {t('inventoryAdjustments.addRow')}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => addLines(3)}>
                  {t('inventoryAdjustments.add3Rows')}
                </Button>
                <Button type="button" variant="outline" size="sm" onClick={() => addLines(5)}>
                  {t('inventoryAdjustments.add5Rows')}
                </Button>
              </div>
            )}
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-b-2 border-primary/20 hover:bg-transparent">
                    <TableHead className={tableHeaderClass}>{t('inventoryAdjustments.colItem')}</TableHead>
                    <TableHead className={tableHeaderClass}>{t('inventoryAdjustments.colBatch')}</TableHead>
                    <TableHead className={`${tableHeaderClass} text-center`}>
                      {t('inventoryAdjustments.colCurrentQty')}
                    </TableHead>
                    <TableHead className={`${tableHeaderClass} text-center`}>
                      {t('inventoryAdjustments.colNewQty')}
                    </TableHead>
                    <TableHead className={`${tableHeaderClass} text-center`}>
                      {t('inventoryAdjustments.colQtyAdjusted')}
                    </TableHead>
                    {!viewOnly && (
                      <TableHead className={`${tableHeaderClass} w-[64px] text-center`}>
                        {t('common.delete')}
                      </TableHead>
                    )}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {lines.map((line, index) => {
                    const whNum = Number(warehouseId)
                    const lineBatches = batchesByLineId[line.id] ?? []
                    const batchLoading = batchesLoadingByLineId[line.id] ?? false
                    const qtyLoading = qtyLoadingByLineId[line.id] ?? false
                    const itemLabel =
                      line.itemId ? itemOptions.find((o) => o.value === line.itemId)?.label ?? line.itemId : ''
                    const batchRow = lineBatches.find((b) => String(b.id) === line.batchId)
                    const batchLabelStr = batchRow ? batchLabel(batchRow) : line.batchId || ''
                    return (
                    <TableRow key={line.id}>
                      <TableCell className="align-top py-2">
                        {viewOnly ? (
                          <Input value={itemLabel} readOnly className={`h-9 ${readonlyFieldClass}`} />
                        ) : (
                        <Select
                          value={line.itemId ? line.itemId : SELECT_EMPTY}
                          onValueChange={(v) => handleItemChange(index, v)}
                          disabled={
                            !whNum ||
                            warehouseId === SELECT_EMPTY ||
                            itemsLoading
                          }
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder={t('inventoryAdjustments.selectItem')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={SELECT_EMPTY}>
                              {!whNum || warehouseId === SELECT_EMPTY
                                ? t('unsellableInventory.selectWarehouseForItems')
                                : itemsLoading
                                  ? t('common.loading', { defaultValue: 'Loading…' })
                                  : t('inventoryAdjustments.selectItem')}
                            </SelectItem>
                            {itemOptions.map((o) => (
                              <SelectItem key={o.value} value={o.value}>
                                {o.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        )}
                      </TableCell>
                      <TableCell className="align-top py-2">
                        {viewOnly ? (
                          <Input value={batchLabelStr} readOnly className={`h-9 ${readonlyFieldClass}`} />
                        ) : (
                        <Select
                          value={line.batchId ? line.batchId : SELECT_EMPTY}
                          onValueChange={(v) => handleBatchChange(index, v)}
                          disabled={
                            !whNum ||
                            warehouseId === SELECT_EMPTY ||
                            !line.itemId ||
                            batchLoading
                          }
                        >
                          <SelectTrigger className="h-9">
                            <SelectValue placeholder={t('inventoryAdjustments.selectBatch')} />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value={SELECT_EMPTY}>
                              {!line.itemId
                                ? t('unsellableInventory.selectWarehouseItemForBatches')
                                : batchLoading
                                  ? t('common.loading', { defaultValue: 'Loading…' })
                                  : t('inventoryAdjustments.selectBatch')}
                            </SelectItem>
                            {lineBatches.map((b) => (
                              <SelectItem key={b.id} value={String(b.id)}>
                                {batchLabel(b)}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        )}
                      </TableCell>
                      <TableCell className="py-2 text-center align-top">
                        <Input
                          readOnly
                          className={`h-9 text-center ${viewOnly ? readonlyFieldClass : 'bg-muted/50'}`}
                          title={
                            qtyLoading ? t('unsellableInventory.currentQtyLoadingHint') : undefined
                          }
                          aria-busy={qtyLoading}
                          value={
                            !line.batchId
                              ? ''
                              : qtyLoading
                                ? ''
                                : String(line.currentQty)
                          }
                        />
                      </TableCell>
                      <TableCell className="py-2 text-center align-top">
                        <Input
                          type="number"
                          className={`h-9 text-center ${viewOnly ? readonlyFieldClass : ''}`}
                          min={0}
                          step="any"
                          readOnly={viewOnly}
                          value={line.batchId ? line.newQty : ''}
                          onChange={(e) => {
                            const raw = e.target.value
                            if (raw === '') {
                              updateLineNewQty(index, 0)
                              return
                            }
                            const n = Number(raw)
                            if (Number.isFinite(n)) updateLineNewQty(index, n)
                          }}
                        />
                      </TableCell>
                      <TableCell className="py-2 text-center align-top">
                        <Input
                          readOnly
                          className={`h-9 text-center ${viewOnly ? readonlyFieldClass : 'bg-muted/50'}`}
                          value={
                            line.itemId && line.batchId
                              ? formatQuantityAdjusted(quantityAdjusted(line))
                              : ''
                          }
                        />
                      </TableCell>
                      {!viewOnly && (
                        <TableCell className="py-2 text-center align-top">
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="text-destructive hover:bg-destructive/10"
                            onClick={() => removeLine(index)}
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      )}
                    </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        <div className="flex flex-col gap-3 sm:flex-row sm:justify-end sm:items-center sm:gap-2">
          <Button type="button" variant="outline" onClick={handleBack} className="w-full sm:w-auto">
            <ArrowLeft className="mr-2 h-4 w-4" />
            {t('common.back')}
          </Button>
          {!viewOnly && (
            <Button type="submit" disabled={saveLoading || detailLoading} className="w-full sm:w-auto">
              <Save className="mr-2 h-4 w-4" />
              {saveLoading
                ? t('common.saving', { defaultValue: 'Saving…' })
                : !isNew
                  ? t('common.update')
                  : t('inventoryAdjustments.save')}
            </Button>
          )}
        </div>
      </form>
    </div>
  )
}
